import { hasObj } from 'lib/util';
import { queryString } from 'lib/format';
import { ContentStatus } from 'enums';
import pickerStreamsSelector from 'common/selectors/pickerStreamsSelector';
import selectedContentSelector from 'common/selectors/selectedContentSelector';
import { getPlayerUrl, filterPickerStreamsById } from 'lib/streamPicker';
import { fetchPickerStreams } from 'common/actions/apiAction';
import {
    streamUnSelect,
    streamSelect,
    openPickerSuccess,
    openPickerError,
    pickerStateClear,
    closeModalMessage,
    pickerStreamsUnSelect
} from 'common/actions/appActions';
import { getFirstStreamUserAuth, canPlayStream, authStateKnown } from 'lib/auth';

const MAX_RETRIES = 5;
const RETRY_WAIT_TIME = 1000;

export function getPickerStreams(content, pickerStreams) {
    return async (dispatch, getState) => {
        const state = getState();
        const nextSelectedContent = selectedContentSelector(state);
        let selectPickerStreams = pickerStreams;
        // new content is selected
        if (nextSelectedContent.id !== content.id) {
            selectPickerStreams = pickerStreamsSelector(state);
        }
        const { links = {}, eventId } = nextSelectedContent;        
        const { pickerStreamsEventId } = state.app;

        // if id is equal to pickerStreamsEventId, than means we had pickerStreams
        if ( selectPickerStreams && selectPickerStreams.length && pickerStreamsEventId === eventId ) {
            return {
                nextSelectedContent,
                nextPickerStreams: selectPickerStreams
            }
        }
        // otherwise; let's trigger a new picker pageApi call and fetch pickerStreams data 
        const pickerApiUrl = links.picker;
        if (pickerApiUrl) {
            dispatch(pickerStreamsUnSelect());
            await dispatch(fetchPickerStreams(pickerApiUrl, eventId));
            const nextStreams = pickerStreamsSelector(getState());
            return {
                nextSelectedContent,
                nextPickerStreams: nextStreams
            }
        }
        return {};
    }
}
export async function handleSelectedStream(props) {            
    const {
        content, 
        route, 
        urlParams, 
        pickerStreams, 
        airingId, 
        dispatch, 
        authRes: { userAuth = [] }
    } = props
    const { router, rState, locationBeforeTransitions = {} } = route;
    const { basename, pathname = '', query = {} } = locationBeforeTransitions;
    
    const { useqa, id, _spHost, eventCalendarId, ...resQs,  } = query;
    let qs = {};
    if (useqa) {
        /**
         * if useqa is passed in the query, we want to keep the rest of query string
         * except _spHost for testing purpose in QA
         */
        qs = { useqa, ...resQs };
    }
    const { nextPickerStreams, nextSelectedContent } = await dispatch(getPickerStreams(content, pickerStreams));
    const streamFromPicker = filterPickerStreamsById(nextPickerStreams, airingId);
    const { placement, status } = content;
    const onHero = placement === 'inline-hero';
    if (streamFromPicker && Array.isArray(streamFromPicker.streams)) {
        // Avoids the `reselected` return logic when clicking the same stream
        const stream = streamFromPicker.streams[0]
        const canPlay = canPlayStream(stream, userAuth, false);
        if ( !canPlay && (onHero || eventCalendarId && nextSelectedContent.id === eventCalendarId)) {
            /**
             * ticket: https://jira.disney.com/browse/WEWEB-2684
             * When multicast eventCalendarId is passed in the url, user is logged to MVPD,
             * BC player is embed and user is not logged to E+ and select E+ content, or if user is
             * logged to E+, DSS player is embed and user is not logged to MVPD and select TVE content,
             * we need to trigger url update with selected stream id from the picker.
             * Auth flow will be triggered in handleSingleStreamAuthentication (selectStreamForAuthUsers.js)
             */
            qs.id = stream.id;
            window.location.href = `${basename}${pathname}${queryString(qs)}`;
        } else {
            dispatch(streamUnSelect());
            stream.feedSwitch = true;
            dispatch(closeModalMessage());
            dispatch(streamSelect(stream));

            /**
             * urlParams is passed when user clicks on tiles from
             * clickHandler event, file app/webapp/common/playable.js.
             * Before this update, for all multi streams, user didn't give an option to
             * select the streams after they logged it, so we will enforce to open the picker
             * even if user is logged it.
             */
            if (urlParams) {
                const useLocationPathname = (onHero || ((id || eventCalendarId) && !urlParams)) && status !== ContentStatus.UPCOMING;
                urlParams.content = nextSelectedContent;    
                const playerUrl = getPlayerUrl({ useLocationPathname, ...urlParams }); 
                // if playerUrl is set, re-route the page to the playerUrl
                if (playerUrl.query) {
                    playerUrl.query = {
                        ...qs,
                        ...playerUrl.query,
                        airingId
                    } 
                }
                router.push({
                    ...playerUrl,
                    state: {pickerStreams: nextPickerStreams, ...rState}
                });
            }
        }
    } else {
        handleStreamPickerOnError(props);
    }    
}

export function onRowClickCallback(props) {
    // close the picker as soon as use clicks on the row.
    espn_ui.Helpers.streamPicker.closePicker();
    handleSelectedStream(props);
    
    window.jQuery.unsubscribe && window.jQuery.unsubscribe(window.espn_ui.Helpers.streamPicker.evts.ON_ROW_CLICK);
}

export function isPickerReady() {
    const hasSubscribe = hasObj(window, 'jQuery.subscribe');
    const hasRowClick = hasObj(window, 'espn_ui.Helpers.streamPicker.evts.ON_ROW_CLICK');
    const hasOpenPicker = hasObj(window, 'espn_ui.Helpers.streamPicker.openPicker');
    return hasSubscribe && hasRowClick && hasOpenPicker;
}

export function handleStreamPickerOnError(props) {
    const {
        content,
        dispatch,
        urlParams,
        route,
        authRes: { userAuth = [] }
    } = props;
    const { streams = [] } = content;
    const selectedStream = getFirstStreamUserAuth(streams, userAuth) || streams[0]; 
    if (selectedStream) {
        dispatch(streamUnSelect());
        selectedStream.feedSwitch = true;
        dispatch(closeModalMessage());
        dispatch(streamSelect(selectedStream));

        if (urlParams) {
            const { router, rState } = route;
            const playerUrl = getPlayerUrl({...urlParams });
            router.push({
                ...playerUrl,
                state: {...rState}
            });
        }
    }
}

// check .com stream picker script functions are available in the dom
export async function waitUntilPickerScriptReady(isPickerReady, maxReTries, waitTime) {
    let retry = 0;
    return await new Promise(resolve => {
      const reTryOpenPicker = setInterval(() => {
        if (retry < maxReTries) {
            const isReady = isPickerReady();
            if (isReady ) {
                resolve(true);
                clearInterval(reTryOpenPicker);
            }
            
        } else {
            clearInterval(reTryOpenPicker);
            resolve(false);
        }
        retry += 1;
      }, waitTime);
    });
}

/**
 * @description:
 * Open .com stream picker which is an iframe that uses jQuery pub/sub.
 * We need to check if .com jQuery events are available before calling it.
 * We check every 500ms up to 4 times, total of 3 seconds. If .com jQuery events
 * are available, we call window.espn_ui.Helpers.streamPicker.openPicker and
 * dispatch openPickerSuccess; otherwise, dispatch openPickerError
 * 
 * @param {object} content   // content.id will be needed for the triggering stream picker
 * @param {object} route     // empty object or route state object of { router, playerUrl, rState }
 * @param {object} dispatch  // store dispatch
 * @param {object} authRes   // empty object or includes userAuth and preauthorizedResources props
 */
export async function openStreamPicker(props) {
    const {
        content, 
        route = {}, 
        dispatch,
        country = '',
        lang = '',
        pickerStreams = [],
        authRes: {
            userAuth = [], 
            preauthorizedResources = []
        }
    } = props;
    
    let retry = 0;
    dispatch(pickerStateClear());  // clear previous picker state 

    const { pathname = '', query: { eventCalendarId } } =  route.locationBeforeTransitions;
    const darkmode =  pathname.indexOf('/player') !== -1;
    
    /**
     * we need to fetch pickerStreams data for in-player stream picker, 
     * in case when user closes stream picker without picking a stream on pageload
     */
    if (eventCalendarId && pickerStreams && !pickerStreams.length) {
        const { links = {}, eventId } = content;
        // use == not === to compare value only between eventCalendarId and eventId
        if (eventCalendarId == eventId && links.picker) {
            const { nextPickerStreams } = await dispatch(getPickerStreams(content, pickerStreams));
            props.pickerStreams = nextPickerStreams;            
        }
    }

    let pickerScriptReady;
    await waitUntilPickerScriptReady(isPickerReady, MAX_RETRIES, RETRY_WAIT_TIME).then( res => (pickerScriptReady = res))

    if (pickerScriptReady) {
        try {                   
            if (content && content.id) {
               
                window.espn_ui.Helpers.streamPicker.openPicker({
                    eventId: content.id,
                    triggerEvtOnNav: true,
                    entitlements: userAuth,
                    tvePreauthResources: preauthorizedResources,
                    countryCode: country,
                    lang,
                    darkmode
                }).then(function() {
                    window.jQuery.subscribe(
                        window.espn_ui.Helpers.streamPicker.evts.ON_ROW_CLICK, res => onRowClickCallback({airingId: res.airing.id, ...props})
                    );
                    dispatch(openPickerSuccess({id: content.id, retryTime: retry}));
                }).catch(function() {
                    dispatch(openPickerError(`Open picker error: openPicker failed, retryTime ${retry}`));
                    window.espn_ui.Helpers.streamPicker.closePicker();
                    handleStreamPickerOnError(props);
                });
            }
        } catch(err) {
            dispatch(openPickerError(`Open picker error: ${err.message}`));
        }
    } else {
        handleStreamPickerOnError(props);
    }
}


// WEBPACK FOOTER //
// ./app/lib/streamPicker/openStreamPicker.js